/**
 * Calculator Cache Service
 * Implements a multi-tier caching strategy for calculator data with TTL (Time-To-Live)
 */

// Constants
const MEMORY_CACHE_TTL = {
  RESULTS: 30 * 60 * 1000, // 30 minutes for calculation results
  HISTORY: 60 * 60 * 1000, // 1 hour for calculation history
  PRESETS: 24 * 60 * 60 * 1000, // 24 hours for calculator presets
};

const LOCAL_STORAGE_TTL = {
  RESULTS: 7 * 24 * 60 * 60 * 1000, // 7 days for calculation results
  HISTORY: 30 * 24 * 60 * 60 * 1000, // 30 days for calculation history
  PRESETS: 90 * 24 * 60 * 60 * 1000, // 90 days for calculator presets
};

const LOCAL_STORAGE_KEYS = {
  CALCULATOR_RESULTS: 'calculator_results_cache',
  CALCULATOR_HISTORY: 'calculator_history',
  CALCULATOR_PRESETS: 'calculator_presets',
};

// In-memory cache
const memoryCache = {
  results: new Map(),
  history: new Map(),
  presets: new Map(),
};

/**
 * Determines if a cached item has expired
 * @param {Object} cacheItem - The cached item with timestamp
 * @param {number} ttl - Time to live in milliseconds
 * @returns {boolean} True if the cache has expired
 */
const isCacheExpired = (cacheItem, ttl) => {
  if (!cacheItem || !cacheItem.timestamp) return true;
  return Date.now() - cacheItem.timestamp > ttl;
};

/**
 * Creates a cache item with the current timestamp
 * @param {*} data - The data to cache
 * @returns {Object} The cache item with data and timestamp
 */
const createCacheItem = (data) => ({
  data,
  timestamp: Date.now(),
});

/**
 * Saves data to localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {*} data - The data to store
 */
const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    // If quota exceeded, try to clear some space
    if (error.name === 'QuotaExceededError') {
      try {
        // Clear older cache items
        localStorage.removeItem(LOCAL_STORAGE_KEYS.CALCULATOR_RESULTS);
      } catch (e) {
        console.error('Failed to clear space in localStorage:', e);
      }
    }
  }
};

/**
 * Retrieves data from localStorage with error handling
 * @param {string} key - The localStorage key
 * @returns {Object|null} The retrieved data or null if not found/error
 */
const getFromLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error retrieving from localStorage:', error);
    return null;
  }
};

/**
 * Cache a calculator result
 * @param {string} calculatorId - The ID of the calculator
 * @param {Object} inputs - The input parameters
 * @param {*} result - The calculation result
 */
export const cacheCalculatorResult = (calculatorId, inputs, result) => {
  if (!calculatorId || !inputs || !result) return;

  // Create a cache key based on calculator ID and inputs
  const cacheKey = getCacheKey(calculatorId, inputs);
  
  // Store in memory cache
  memoryCache.results.set(cacheKey, createCacheItem(result));

  // Store in localStorage
  const storageCache = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_RESULTS) || {};
  storageCache[cacheKey] = createCacheItem(result);
  saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_RESULTS, storageCache);

  // Add to history
  addToCalculatorHistory(calculatorId, inputs, result);
};

/**
 * Get a cached calculator result
 * @param {string} calculatorId - The ID of the calculator
 * @param {Object} inputs - The input parameters
 * @returns {*|null} The cached result or null if not found
 */
export const getCachedCalculatorResult = (calculatorId, inputs) => {
  if (!calculatorId || !inputs) return null;

  const cacheKey = getCacheKey(calculatorId, inputs);
  
  // Try memory cache first (fastest)
  const memoryCacheItem = memoryCache.results.get(cacheKey);
  if (memoryCacheItem && !isCacheExpired(memoryCacheItem, MEMORY_CACHE_TTL.RESULTS)) {
    return memoryCacheItem.data;
  }

  // Try localStorage
  const storageCache = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_RESULTS) || {};
  const storageCacheItem = storageCache[cacheKey];
  if (storageCacheItem && !isCacheExpired(storageCacheItem, LOCAL_STORAGE_TTL.RESULTS)) {
    // Update memory cache with this item
    memoryCache.results.set(cacheKey, storageCacheItem);
    return storageCacheItem.data;
  }

  return null;
};

/**
 * Add an entry to calculator history
 * @param {string} calculatorId - The ID of the calculator
 * @param {Object} inputs - The input parameters
 * @param {*} result - The calculation result
 */
export const addToCalculatorHistory = (calculatorId, inputs, result) => {
  if (!calculatorId) return;

  // Create history entry
  const historyEntry = {
    calculatorId,
    inputs,
    result,
    timestamp: Date.now(),
  };

  // Get history from storage
  const storageHistory = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_HISTORY) || {};
  const calculatorHistory = storageHistory[calculatorId] || [];
  
  // Add new entry (limit to 50 entries per calculator)
  calculatorHistory.unshift(historyEntry);
  if (calculatorHistory.length > 50) {
    calculatorHistory.pop();
  }
  
  // Update storage
  storageHistory[calculatorId] = calculatorHistory;
  saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_HISTORY, storageHistory);
  
  // Update memory cache
  memoryCache.history.set(calculatorId, createCacheItem(calculatorHistory));
};

/**
 * Get calculator history
 * @param {string} calculatorId - The ID of the calculator
 * @returns {Array} The calculation history for this calculator
 */
export const getCalculatorHistory = (calculatorId) => {
  if (!calculatorId) return [];

  // Try memory cache first
  const memoryCacheItem = memoryCache.history.get(calculatorId);
  if (memoryCacheItem && !isCacheExpired(memoryCacheItem, MEMORY_CACHE_TTL.HISTORY)) {
    return memoryCacheItem.data;
  }

  // Try localStorage
  const storageHistory = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_HISTORY) || {};
  const calculatorHistory = storageHistory[calculatorId] || [];
  
  // Update memory cache
  memoryCache.history.set(calculatorId, createCacheItem(calculatorHistory));
  
  return calculatorHistory;
};

/**
 * Clear calculator history
 * @param {string} calculatorId - The ID of the calculator (optional, if not provided clears all history)
 */
export const clearCalculatorHistory = (calculatorId) => {
  if (!calculatorId) {
    // Clear all history
    memoryCache.history.clear();
    saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_HISTORY, {});
    return;
  }

  // Clear specific calculator history
  memoryCache.history.delete(calculatorId);
  
  const storageHistory = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_HISTORY) || {};
  delete storageHistory[calculatorId];
  saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_HISTORY, storageHistory);
};

/**
 * Save a calculator preset
 * @param {string} calculatorId - The ID of the calculator
 * @param {string} presetName - The name of the preset
 * @param {Object} presetData - The preset data
 */
export const saveCalculatorPreset = (calculatorId, presetName, presetData) => {
  if (!calculatorId || !presetName || !presetData) return;

  // Get presets from storage
  const storagePresets = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_PRESETS) || {};
  const calculatorPresets = storagePresets[calculatorId] || {};
  
  // Add/update preset
  calculatorPresets[presetName] = createCacheItem(presetData);
  
  // Update storage
  storagePresets[calculatorId] = calculatorPresets;
  saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_PRESETS, storagePresets);
  
  // Update memory cache
  memoryCache.presets.set(calculatorId, createCacheItem(calculatorPresets));
};

/**
 * Get calculator presets
 * @param {string} calculatorId - The ID of the calculator
 * @returns {Object} The presets for this calculator
 */
export const getCalculatorPresets = (calculatorId) => {
  if (!calculatorId) return {};

  // Try memory cache first
  const memoryCacheItem = memoryCache.presets.get(calculatorId);
  if (memoryCacheItem && !isCacheExpired(memoryCacheItem, MEMORY_CACHE_TTL.PRESETS)) {
    return memoryCacheItem.data;
  }

  // Try localStorage
  const storagePresets = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_PRESETS) || {};
  const calculatorPresets = storagePresets[calculatorId] || {};
  
  // Filter out expired presets
  const validPresets = {};
  for (const [name, preset] of Object.entries(calculatorPresets)) {
    if (!isCacheExpired(preset, LOCAL_STORAGE_TTL.PRESETS)) {
      validPresets[name] = preset;
    }
  }
  
  // Update memory cache
  memoryCache.presets.set(calculatorId, createCacheItem(validPresets));
  
  // Update storage if some presets were filtered out
  if (Object.keys(validPresets).length !== Object.keys(calculatorPresets).length) {
    storagePresets[calculatorId] = validPresets;
    saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_PRESETS, storagePresets);
  }
  
  return validPresets;
};

/**
 * Delete a calculator preset
 * @param {string} calculatorId - The ID of the calculator
 * @param {string} presetName - The name of the preset to delete
 */
export const deleteCalculatorPreset = (calculatorId, presetName) => {
  if (!calculatorId || !presetName) return;

  // Get presets from storage
  const storagePresets = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_PRESETS) || {};
  const calculatorPresets = storagePresets[calculatorId] || {};
  
  // Delete preset
  delete calculatorPresets[presetName];
  
  // Update storage
  storagePresets[calculatorId] = calculatorPresets;
  saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_PRESETS, storagePresets);
  
  // Update memory cache
  memoryCache.presets.set(calculatorId, createCacheItem(calculatorPresets));
};

/**
 * Generate a cache key from calculator ID and inputs
 * @param {string} calculatorId - The ID of the calculator
 * @param {Object} inputs - The input parameters
 * @returns {string} The cache key
 */
const getCacheKey = (calculatorId, inputs) => {
  // Sort the input keys for consistent cache keys regardless of input order
  const sortedInputs = {};
  Object.keys(inputs).sort().forEach(key => {
    sortedInputs[key] = inputs[key];
  });
  
  return `${calculatorId}_${JSON.stringify(sortedInputs)}`;
};

/**
 * Perform cache cleanup on initialization
 * Removes expired items from localStorage to prevent storage bloat
 */
const initializeCache = () => {
  // Clean up calculator results cache
  const resultsCache = getFromLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_RESULTS) || {};
  let hasChanges = false;
  
  for (const [key, item] of Object.entries(resultsCache)) {
    if (isCacheExpired(item, LOCAL_STORAGE_TTL.RESULTS)) {
      delete resultsCache[key];
      hasChanges = true;
    }
  }
  
  if (hasChanges) {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.CALCULATOR_RESULTS, resultsCache);
  }
  
  // Additional cleanup tasks can be added here for history and presets
};

// Run cleanup on load
initializeCache();

export default {
  cacheCalculatorResult,
  getCachedCalculatorResult,
  getCalculatorHistory,
  clearCalculatorHistory,
  saveCalculatorPreset,
  getCalculatorPresets,
  deleteCalculatorPreset
};